import { useSearchParams } from 'react-router-dom';

import { coQuyen, useToi } from '../../../lib/auth.js';
import { Nut, PageHeader, Tabs } from '../../../ui/index.js';
import { useNhom, useTomTatTeam } from '../api.js';
import { DialogMoiThanhVien } from '../components/DialogMoiThanhVien.js';
import { HopNhom } from '../components/HopNhom.js';
import { TabHoSoThue } from '../components/TabHoSoThue.js';
import { TabNhom } from '../components/TabNhom.js';
import { TabThanhVien } from '../components/TabThanhVien.js';
import { useState } from 'react';

const TAB = ['thanh-vien', 'nhom', 'ho-so-thue'] as const;
type Tab = (typeof TAB)[number];

const NHAN_TAB: Record<Tab, string> = {
  'thanh-vien': 'Thành viên',
  nhom: 'Nhóm',
  'ho-so-thue': 'Hồ sơ thuế',
};

/** Kỳ tháng hiện tại — badge số nhóm đọc theo kỳ này. */
function thangHienTai(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Nhân sự & nhóm — **một** trang ba tab, gộp từ ba trang của bước 2:
 * "Nhân sự & phân quyền", "Nhóm & đội ngũ" và "Hồ sơ thuế nhân sự".
 *
 * Ba trang đó nói về cùng một tập người: ai làm việc ở đây, họ thuộc nhóm nào,
 * và trả tiền cho họ thì khai thuế thế nào. Tách thành ba mục menu khiến người
 * dùng phải nhớ "tỷ lệ trưởng nhóm nằm ở trang nào trong ba trang nhân sự" —
 * nay là ba tab cạnh nhau. Route cũ **vẫn mở được**: chúng chuyển hướng sang
 * `?tab=` tương ứng (xem `module.ts`).
 *
 * Tab **Hồ sơ thuế** chỉ `team:admin` thấy: nó bày CCCD, MST và số tài khoản
 * của từng người.
 */
export function TrangNhanSuVaNhom() {
  const toi = useToi();
  const laAdmin = coQuyen(toi.data?.permissions, 'team', 'admin');

  const [thamSo, datThamSo] = useSearchParams();
  const tomTat = useTomTatTeam();
  const nhom = useNhom(thangHienTai());

  const [moMoi, setMoMoi] = useState(false);
  const [taoNhom, setTaoNhom] = useState(false);

  const duocXem = (t: Tab) => t !== 'ho-so-thue' || laAdmin;

  const tho = thamSo.get('tab') ?? '';
  /* Tab lạ, hoặc tab người này không được xem, thì về tab đầu. */
  const tab: Tab = TAB.includes(tho as Tab) && duocXem(tho as Tab) ? (tho as Tab) : 'thanh-vien';

  const doiTab = (moi: string) => {
    const tiep = new URLSearchParams(thamSo);
    tiep.set('tab', moi);
    datThamSo(tiep);
  };

  const phu: Record<Tab, string> = {
    'thanh-vien': 'Ai làm việc ở đây, giữ quyền gì và tình trạng bảo mật tài khoản',
    nhom: 'Nhóm kênh, trưởng nhóm hưởng phần trăm trên từng kênh, và chính sách khi kênh lỗ',
    'ho-so-thue': 'Bên B của biên bản chia lợi nhuận và diện thuế TNCN của từng người',
  };

  return (
    <>
      <PageHeader
        title="Nhân sự & nhóm"
        phu={phu[tab]}
        {...(tab === 'ho-so-thue'
          ? {
              giaiThich:
                'Số định danh hiện ở đây đã che bớt; chỉ biên bản in đầy đủ. Dữ liệu CCCD, MST và số tài khoản lưu dạng mã hoá trong database.',
            }
          : {})}
        actions={
          laAdmin && tab === 'thanh-vien' ? (
            <Nut
              kieu="chinh"
              onClick={() => {
                setMoMoi(true);
              }}
            >
              + Mời thành viên
            </Nut>
          ) : laAdmin && tab === 'nhom' ? (
            <Nut
              kieu="chinh"
              onClick={() => {
                setTaoNhom(true);
              }}
            >
              + Tạo nhóm
            </Nut>
          ) : undefined
        }
      />

      <div className="mb-4">
        <Tabs
          aria-label="Phần của trang Nhân sự & nhóm"
          value={tab}
          onChange={doiTab}
          items={TAB.filter(duocXem).map((t) => ({
            id: t,
            label: NHAN_TAB[t],
            ...(t === 'thanh-vien' && tomTat.data ? { badge: tomTat.data.members } : {}),
            ...(t === 'nhom' && nhom.data ? { badge: nhom.data.length } : {}),
          }))}
        />
      </div>

      {tab === 'thanh-vien' && <TabThanhVien />}
      {tab === 'nhom' && <TabNhom />}
      {tab === 'ho-so-thue' && <TabHoSoThue />}

      {moMoi && (
        <DialogMoiThanhVien
          onDong={() => {
            setMoMoi(false);
          }}
        />
      )}

      {taoNhom && (
        <HopNhom
          onDong={() => {
            setTaoNhom(false);
          }}
        />
      )}
    </>
  );
}
